/**
 * OrganizerActivityLog.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The organizer's who-did-what timeline: filterable by actor, action, resource
 * and date range, paginated server-side.
 *
 * Same visual language as views/RBACManager.js and OrganizerTeam.js. Two house
 * rules repeated here because they are easy to lose in a rewrite:
 *   - wide tables get their OWN `overflow-x: auto` wrapper; `<Table responsive>`
 *     does not scroll on this theme above 992px
 *   - no `confirm()`, no emojis
 *
 * The rows are append-only server-side. There is deliberately no edit or delete
 * control anywhere in this file.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { apiTicketingMyOrganizers } from "../api/index.js";
import {
  apiOrganizerActivity,
  apiOrganizerActivityActors,
} from "../api/organizerRbac.js";
import AppSelect from "../components/ui/AppSelect";

// ---------------------------------------------------------------------------
// STYLES -- mirrors views/RBACManager.js
// ---------------------------------------------------------------------------

const S = {
  page: { background: "#f8fafc", minHeight: "100vh", padding: "24px 20px", fontFamily: "'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 16, flexWrap: "wrap" },
  title: { fontSize: 26, fontWeight: 700, color: "#0f172a", margin: 0 },
  subtitle: { fontSize: 13, color: "#64748b", marginTop: 4 },
  card: { background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: 20, marginBottom: 16 },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 12 },
  filterGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 12, alignItems: "end" },
  tableWrap: { overflowX: "auto", WebkitOverflowScrolling: "touch", width: "100%" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 920 },
  th: { textAlign: "left", padding: "10px 12px", background: "#f1f5f9", fontWeight: 600, color: "#475569", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" },
  td: { padding: "12px 12px", borderBottom: "1px solid #f1f5f9", color: "#1e293b", verticalAlign: "top" },
  badge: (bg, color) => ({ display: "inline-flex", alignItems: "center", gap: 4, background: bg, color, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: "0.03em" }),
  pill: (color, bg) => ({ display: "inline-block", padding: "2px 9px", borderRadius: 12, background: bg, color, fontSize: 11, fontWeight: 700 }),
  btnPrimary: { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnGhost: { background: "transparent", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 7, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  input: { border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 12px", fontSize: 13, width: "100%", outline: "none", boxSizing: "border-box" },
  select: { border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 12px", fontSize: 13, background: "#fff", cursor: "pointer", outline: "none", boxSizing: "border-box", width: "100%" },
  label: { fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4, display: "block" },
  alert: (type) => ({ padding: "12px 16px", borderRadius: 8, marginBottom: 14, fontSize: 13, background: type === "error" ? "#fef2f2" : "#eff6ff", color: type === "error" ? "#dc2626" : "#1d4ed8", border: `1px solid ${type === "error" ? "#fecaca" : "#bfdbfe"}` }),
  code: { fontSize: 11, background: "#f1f5f9", padding: "2px 6px", borderRadius: 4, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  diffBox: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 7, padding: 10, marginTop: 8, fontSize: 11, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 240, overflow: "auto" },
  spinner: { display: "inline-block", width: 18, height: 18, border: "2px solid #e2e8f0", borderTopColor: "#1d4ed8", borderRadius: "50%", animation: "spin 0.6s linear infinite" },
};

// Colour families by what the action touches: money and access changes should
// not look the same as a routine content edit.
const ACTION_COLOR = (action = "") => {
  if (action.startsWith("MEMBER_") || action.startsWith("ROLE_")) return ["#7c3aed", "#ede9fe"];
  if (action === "PERMISSION_DENIED") return ["#dc2626", "#fee2e2"];
  if (["TICKET_VOIDED", "REFUND_DECIDED", "SHOWING_CANCELLED", "BOX_OFFICE_SALE"].includes(action))
    return ["#b45309", "#fef3c7"];
  if (action === "GATE_SCAN") return ["#0369a1", "#e0f2fe"];
  return ["#1d4ed8", "#dbeafe"];
};

const humanAction = (action = "") =>
  action
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-UG", { dateStyle: "medium", timeStyle: "short" }) : "-";
const apiErrMsg = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

const EMPTY_FILTERS = {
  actorUserId: "",
  action: "",
  resourceType: "",
  resourceId: "",
  outcome: "",
  from: "",
  to: "",
};

const Spinner = () => <span style={S.spinner} />;

const Btn = ({ variant = "ghost", onClick, disabled, children, style }) => (
  <button
    type="button"
    style={{ ...(variant === "primary" ? S.btnPrimary : S.btnGhost), ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}), ...style }}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <div>
    <label style={S.label}>{label}</label>
    {children}
  </div>
);

const Pagination = ({ page, pages, onPage }) => {
  if (pages <= 1) return null;
  // Named pageWindow, not `window` -- shadowing the global in a browser file
  // is the kind of thing that reads fine and bites six months later.
  const pageWindow = Array.from({ length: Math.min(pages, 7) }, (_, i) => {
    const start = Math.max(1, Math.min(page - 3, pages - 6));
    return start + i;
  }).filter((p) => p >= 1 && p <= pages);
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16, flexWrap: "wrap" }}>
      <Btn disabled={page === 1} onClick={() => onPage(page - 1)}>Prev</Btn>
      {pageWindow.map((p) => (
        <Btn key={p} style={p === page ? { background: "#1d4ed8", color: "#fff", borderColor: "#1d4ed8" } : {}} onClick={() => onPage(p)}>
          {p}
        </Btn>
      ))}
      <Btn disabled={page === pages} onClick={() => onPage(page + 1)}>Next</Btn>
    </div>
  );
};

/**
 * before/after are already redacted server-side (`redactActivityPayload`), so
 * anything sensitive arrives as [REDACTED]. This renderer never has to make a
 * judgement about what is safe to show.
 */
const DetailBlock = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = entry.before != null || entry.after != null;
  if (!hasDetail) return null;
  return (
    <div>
      <Btn style={{ padding: "3px 9px", fontSize: 11, marginTop: 6 }} onClick={() => setOpen((o) => !o)}>
        {open ? "Hide detail" : "Show detail"}
      </Btn>
      {open && (
        <div style={S.diffBox}>
          {entry.before != null && (
            <div>
              <strong>Before</strong>
              {"\n"}
              {JSON.stringify(entry.before, null, 2)}
              {"\n\n"}
            </div>
          )}
          {entry.after != null && (
            <div>
              <strong>After</strong>
              {"\n"}
              {JSON.stringify(entry.after, null, 2)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OrganizerActivityLog = () => {
  const [organizers, setOrganizers] = useState([]);
  const [organizerId, setOrganizerId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [entries, setEntries] = useState([]);
  const [pages, setPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [knownActions, setKnownActions] = useState([]);
  const [actors, setActors] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiTicketingMyOrganizers();
        const list = res.data?.organizers || [];
        if (cancelled) return;
        setOrganizers(list);
        setOrganizerId((current) => current || (list[0] ? String(list[0]._id) : ""));
      } catch (e) {
        if (!cancelled) setError(apiErrMsg(e, "Failed to load your organizers."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!organizerId) return;
    setLoading(true);
    setError("");
    try {
      const params = { page, limit };
      for (const [k, v] of Object.entries(applied)) if (v) params[k] = v;
      const res = await apiOrganizerActivity(organizerId, params);
      const data = res.data?.data || {};
      setEntries(data.entries || []);
      setPages(data.pages || 0);
      setTotal(data.total || 0);
      setKnownActions(data.knownActions || []);
    } catch (e) {
      setError(apiErrMsg(e, "Failed to load the activity log."));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [organizerId, applied, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    if (!organizerId) return undefined;
    (async () => {
      try {
        const res = await apiOrganizerActivityActors(organizerId);
        if (!cancelled) setActors(res.data?.data?.actors || []);
      } catch {
        if (!cancelled) setActors([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizerId]);

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  const apply = () => {
    setPage(1);
    setApplied(filters);
  };

  const reset = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };

  const activeFilterCount = useMemo(
    () => Object.values(applied).filter(Boolean).length,
    [applied]
  );

  const resourceTypes = useMemo(
    () => [...new Set(entries.map((e) => e.resourceType).filter(Boolean))],
    [entries]
  );

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Activity log</h2>
          <div style={S.subtitle}>
            Every change made on this organizer, who made it, and from where. Append-only.
          </div>
        </div>
        {organizers.length > 1 && (
          <AppSelect
            style={{ ...S.select, width: "auto", minWidth: 220 }}
            value={organizerId}
            onChange={(e) => {
              setOrganizerId(e.target.value);
              setPage(1);
            }}
          >
            {organizers.map((o) => (
              <option key={o._id} value={o._id}>
                {o.name}
              </option>
            ))}
          </AppSelect>
        )}
      </div>

      {error ? <div style={S.alert("error")}>{error}</div> : null}

      <div style={S.card}>
        <div style={S.sectionTitle}>
          Filters{activeFilterCount ? ` (${activeFilterCount} active)` : ""}
        </div>
        <div style={S.filterGrid}>
          <Field label="Who">
            <AppSelect style={S.select} value={filters.actorUserId} onChange={setFilter("actorUserId")}>
              <option value="">Anyone</option>
              {actors.map((a) => (
                <option key={a.userId} value={a.userId}>
                  {a.name || a.email || a.userId}
                </option>
              ))}
            </AppSelect>
          </Field>
          <Field label="Action">
            <AppSelect style={S.select} value={filters.action} onChange={setFilter("action")}>
              <option value="">Any action</option>
              {knownActions.map((a) => (
                <option key={a} value={a}>
                  {humanAction(a)}
                </option>
              ))}
            </AppSelect>
          </Field>
          <Field label="Resource type">
            <AppSelect style={S.select} value={filters.resourceType} onChange={setFilter("resourceType")}>
              <option value="">Any resource</option>
              {resourceTypes.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </AppSelect>
          </Field>
          <Field label="Resource id">
            <input style={S.input} value={filters.resourceId} onChange={setFilter("resourceId")} placeholder="Exact id" />
          </Field>
          <Field label="Outcome">
            <AppSelect style={S.select} value={filters.outcome} onChange={setFilter("outcome")}>
              <option value="">Any outcome</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILURE">Failure</option>
              <option value="DENIED">Denied</option>
            </AppSelect>
          </Field>
          <Field label="From">
            <input type="date" style={S.input} value={filters.from} onChange={setFilter("from")} />
          </Field>
          <Field label="To">
            <input type="date" style={S.input} value={filters.to} onChange={setFilter("to")} />
          </Field>
          <Field label="Per page">
            <AppSelect
              style={S.select}
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {[25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </AppSelect>
          </Field>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <Btn variant="primary" onClick={apply}>Apply filters</Btn>
          <Btn onClick={reset} disabled={!activeFilterCount}>Clear</Btn>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.sectionTitle}>
          {total} entr{total === 1 ? "y" : "ies"}
          {loading ? (
            <span style={{ marginLeft: 10 }}>
              <Spinner />
            </span>
          ) : null}
        </div>
        {/* Own scroll container -- <Table responsive> does not scroll on this theme. */}
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>When</th>
                <th style={S.th}>Who</th>
                <th style={S.th}>Action</th>
                <th style={S.th}>What happened</th>
                <th style={S.th}>Resource</th>
                <th style={S.th}>Where from</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => {
                const [color, bg] = ACTION_COLOR(e.action);
                return (
                  <tr key={e._id || `${e.at}-${i}`}>
                    <td style={{ ...S.td, whiteSpace: "nowrap" }}>{fmtDate(e.at)}</td>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{e.actorName || e.actorEmail || "System"}</div>
                      {e.actorRole ? (
                        <div style={{ fontSize: 11, color: "#64748b" }}>{e.actorRole}</div>
                      ) : null}
                    </td>
                    <td style={S.td}>
                      <span style={S.badge(bg, color)}>{humanAction(e.action)}</span>
                      {e.outcome && e.outcome !== "SUCCESS" ? (
                        <div style={{ ...S.pill("#dc2626", "#fee2e2"), marginTop: 4 }}>{e.outcome}</div>
                      ) : null}
                    </td>
                    <td style={{ ...S.td, maxWidth: 380 }}>
                      <div>{e.summary || "-"}</div>
                      {e.permissionKey ? (
                        <div style={{ marginTop: 4 }}>
                          <span style={S.code}>{e.permissionKey}</span>
                        </div>
                      ) : null}
                      <DetailBlock entry={e} />
                    </td>
                    <td style={S.td}>
                      {e.resourceType ? (
                        <div>
                          <div style={{ fontWeight: 600 }}>{e.resourceType}</div>
                          <div style={{ fontSize: 11, color: "#64748b", wordBreak: "break-all" }}>{e.resourceId}</div>
                        </div>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>-</span>
                      )}
                    </td>
                    <td style={S.td}>
                      {e.terminalId ? (
                        <div>
                          <span style={S.pill("#0369a1", "#e0f2fe")}>Terminal</span>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{e.terminalId}</div>
                        </div>
                      ) : (
                        <span style={S.pill("#475569", "#f1f5f9")}>Dashboard</span>
                      )}
                      {e.ip ? <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{e.ip}</div> : null}
                    </td>
                  </tr>
                );
              })}
              {!entries.length && !loading && (
                <tr>
                  <td style={{ ...S.td, textAlign: "center", color: "#94a3b8" }} colSpan={6}>
                    {activeFilterCount
                      ? "Nothing matches those filters."
                      : "No activity recorded yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} onPage={setPage} />
      </div>
    </div>
  );
};

export default OrganizerActivityLog;
